/**
 * Admin Users Page
 * მომხმარებლების ადმინისტრირების გვერდი
 */

import { useState } from 'react';
import { formatDate } from '../../utils/formatDate';
import { useNotification, Card, Spinner } from '@aiia/portal-core';
import {
  useAdminUsers,
  useAdminUserDetails,
  useChangeUserPlan,
  useApplyUserDiscount,
  useSuspendUser,
  useActivateUser,
  useCreateCustomInvoice,
  useAdminUserAnalyticsOverview,
  useAdminUserAnalyticsTraffic,
} from '../../hooks/useAdminUsers';
import { useAdminPlans } from '../../hooks/useAdminPlans';
import { useAdminInvoices } from '../../hooks/useAdminListing';
import {
  useUserFeatureLimits,
  useFeatureCodes,
  useCreateFeatureOverride,
  useUpdateFeatureOverride,
  useDeleteFeatureOverride,
} from '../../hooks/useAdminFeatureOverrides';
import TrendLineChart from '../../components/admin/statistics/TrendLineChart';
import ChannelBarChart from '../../components/admin/statistics/ChannelBarChart';
import PeriodSelector from '../../components/admin/statistics/PeriodSelector';
import type { UserAdminListResponse, FeatureOverrideResponse } from '../../features/api/adminUsers';
import type { PlanAdminResponse } from '../../features/api/adminPlans';
import type { FeatureCodeInfo, FeatureLimitInfo } from '../../features/api/adminFeatureOverrides';
import type { StatisticsPeriod } from '../../features/api/analytics';
import type { InvoiceAdminListResponse } from '../../features/api/adminListing';

function formatNumber(num: number): string {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

type DetailsTab = 'profile' | 'analytics' | 'invoices';

export default function UsersPage() {
  const notification = useNotification();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [page, setPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('profile');
  const [analyticsPeriod, setAnalyticsPeriod] = useState<StatisticsPeriod>('MONTH');
  const [invoicePage, setInvoicePage] = useState(0);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showFeatureLimitsModal, setShowFeatureLimitsModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<string | null>(null);

  const { users, totalElements, totalPages, isLoading, refetch } = useAdminUsers({
    search: search || undefined,
    status: statusFilter || undefined,
    planCode: planFilter || undefined,
    page,
    size: 20,
  });

  const { user: selectedUser, isLoading: loadingDetails } = useAdminUserDetails(selectedUserId);
  const { plans } = useAdminPlans();

  const changeUserPlan = useChangeUserPlan();
  const applyDiscount = useApplyUserDiscount();
  const suspendUser = useSuspendUser();
  const activateUser = useActivateUser();
  const createInvoice = useCreateCustomInvoice();

  // Feature overrides
  const { limits: featureLimits, isLoading: loadingLimits, refetch: refetchLimits } = useUserFeatureLimits(selectedUserId);
  const { featureCodes } = useFeatureCodes();
  const createOverride = useCreateFeatureOverride();
  const updateOverride = useUpdateFeatureOverride();
  const deleteOverride = useDeleteFeatureOverride();

  // Per-user analytics
  const { overview: userAnalyticsOverview, isLoading: loadingAnalyticsOverview } = useAdminUserAnalyticsOverview(
    showDetailsModal && detailsTab === 'analytics' ? selectedUserId : null
  );
  const { traffic: userAnalyticsTraffic, isLoading: loadingAnalyticsTraffic } = useAdminUserAnalyticsTraffic(
    showDetailsModal && detailsTab === 'analytics' ? selectedUserId : null,
    analyticsPeriod
  );

  // Per-user invoices
  const invoicesEnabled = showDetailsModal && detailsTab === 'invoices' && !!selectedUserId;
  const { invoices: userInvoices, totalPages: invoiceTotalPages, isLoading: loadingUserInvoices } = useAdminInvoices(
    invoicesEnabled ? { userId: selectedUserId!, page: invoicePage, size: 10 } : {}
  );

  // Change plan form
  const [newPlanCode, setNewPlanCode] = useState('');
  const [planChangeReason, setPlanChangeReason] = useState('');

  // Discount form
  const [discountPercent, setDiscountPercent] = useState(10);
  const [discountDuration, setDiscountDuration] = useState(1);
  const [discountReason, setDiscountReason] = useState('');

  // Invoice form
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceTargetPlan, setInvoiceTargetPlan] = useState('ENTERPRISE');
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [invoiceCurrency, setInvoiceCurrency] = useState('GEL');
  const [invoiceNotes, setInvoiceNotes] = useState('');

  // Feature override form
  const [overrideFeatureCode, setOverrideFeatureCode] = useState('');
  const [overrideValue, setOverrideValue] = useState(0);
  const [overrideUnlimited, setOverrideUnlimited] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  const handleViewDetails = (user: UserAdminListResponse) => {
    setSelectedUserId(user.id);
    setDetailsTab('profile');
    setAnalyticsPeriod('MONTH');
    setInvoicePage(0);
    setShowDetailsModal(true);
  };

  const handleChangePlan = (userId: string) => {
    setSelectedUserId(userId);
    setNewPlanCode('');
    setPlanChangeReason('');
    setShowChangePlanModal(true);
  };

  const handleApplyDiscount = (userId: string) => {
    setSelectedUserId(userId);
    setDiscountPercent(10);
    setDiscountDuration(1);
    setDiscountReason('');
    setShowDiscountModal(true);
  };

  const handleManageLimits = (userId: string) => {
    setSelectedUserId(userId);
    setEditingFeature(null);
    setOverrideFeatureCode('');
    setOverrideValue(0);
    setOverrideUnlimited(false);
    setOverrideReason('');
    setShowFeatureLimitsModal(true);
  };

  const handleEditFeature = (featureCode: string, currentValue: number, isUnlimited: boolean) => {
    setEditingFeature(featureCode);
    setOverrideFeatureCode(featureCode);
    setOverrideValue(currentValue);
    setOverrideUnlimited(isUnlimited);
    setOverrideReason('');
  };

  const handleSaveOverride = async () => {
    if (!selectedUserId || !overrideFeatureCode) return;
    try {
      if (editingFeature) {
        await updateOverride.mutateAsync({
          userId: selectedUserId,
          featureCode: overrideFeatureCode,
          data: {
            customValue: overrideValue,
            isUnlimited: overrideUnlimited,
            reason: overrideReason || undefined,
          },
        });
      } else {
        await createOverride.mutateAsync({
          userId: selectedUserId,
          data: {
            featureCode: overrideFeatureCode,
            customValue: overrideValue,
            isUnlimited: overrideUnlimited,
            reason: overrideReason || undefined,
          },
        });
      }
      notification.success('Feature limit updated');
      setEditingFeature(null);
      setOverrideFeatureCode('');
      refetchLimits();
    } catch {
      notification.error('Failed to update feature limit');
    }
  };

  const handleRemoveOverride = async (featureCode: string) => {
    if (!selectedUserId) return;
    if (!confirm('Remove this custom limit and revert to plan default?')) return;
    try {
      await deleteOverride.mutateAsync({ userId: selectedUserId, featureCode });
      notification.success('Custom limit removed');
      refetchLimits();
    } catch {
      notification.error('Failed to remove custom limit');
    }
  };

  const handleSuspend = async (user: UserAdminListResponse) => {
    const reason = prompt(`Reason for suspending "${user.email}":`);
    if (!reason) return;

    try {
      await suspendUser.mutateAsync({ userId: user.id, data: { reason } });
      notification.success('User suspended successfully');
      refetch();
    } catch {
      notification.error('Failed to suspend user');
    }
  };

  const handleActivate = async (user: UserAdminListResponse) => {
    try {
      await activateUser.mutateAsync(user.id);
      notification.success('User activated successfully');
      refetch();
    } catch {
      notification.error('Failed to activate user');
    }
  };

  const handleSubmitChangePlan = async () => {
    if (!selectedUserId || !newPlanCode) return;
    try {
      await changeUserPlan.mutateAsync({
        userId: selectedUserId,
        data: { newPlanCode, immediate: true, reason: planChangeReason },
      });
      notification.success('Plan changed successfully');
      setShowChangePlanModal(false);
      refetch();
    } catch {
      notification.error('Failed to change plan');
    }
  };

  const handleSubmitDiscount = async () => {
    if (!selectedUserId) return;
    try {
      await applyDiscount.mutateAsync({
        userId: selectedUserId,
        data: {
          discountPercent,
          durationMonths: discountDuration,
          reason: discountReason,
        },
      });
      notification.success('Discount applied successfully');
      setShowDiscountModal(false);
      refetch();
    } catch {
      notification.error('Failed to apply discount');
    }
  };

  const handleCreateInvoice = (userId: string) => {
    setSelectedUserId(userId);
    setInvoiceTargetPlan('ENTERPRISE');
    setInvoiceAmount(0);
    setInvoiceCurrency('GEL');
    setInvoiceNotes('');
    setShowInvoiceModal(true);
  };

  const handleSubmitInvoice = async () => {
    if (!selectedUserId || !invoiceAmount) return;
    try {
      const result = await createInvoice.mutateAsync({
        userId: selectedUserId,
        data: {
          targetPlanCode: invoiceTargetPlan,
          amount: invoiceAmount,
          currency: invoiceCurrency,
          billingCycle: 'MONTHLY',
          notes: invoiceNotes || undefined,
        },
      });
      notification.success(`Invoice ${result.invoiceNumber} created. Payment link sent via email.`);
      setShowInvoiceModal(false);
      refetch();
    } catch {
      notification.error('Failed to create invoice');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
      case 'SUSPENDED':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Suspended</span>;
      case 'DELETED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Deleted</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getSubscriptionBadge = (status: string | null) => {
    if (!status) return <span className="text-gray-400">-</span>;
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Active</span>;
      case 'TRIAL':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">Trial</span>;
      case 'CANCELED':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Canceled</span>;
      case 'EXPIRED':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Expired</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-600">Manage platform users and their subscriptions</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
          aria-label="Search users by email"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          aria-label="Filter by status"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="DELETED">Deleted</option>
        </select>
        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
          aria-label="Filter by plan"
        >
          <option value="">All Plans</option>
          {plans.map((plan: PlanAdminResponse) => (
            <option key={plan.code} value={plan.code}>{plan.name}</option>
          ))}
        </select>
        <div className="ml-auto text-sm text-gray-500">
          {totalElements} users found
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscription</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user: UserAdminListResponse) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{user.displayName}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(user.status)}</td>
                  <td className="px-6 py-4">{getSubscriptionBadge(user.subscriptionStatus)}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{user.planName || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      {user.widgetCount} widgets, {user.apiKeyCount} keys
                    </div>
                    <div className="text-xs text-gray-400">
                      {user.dataFileCount} files ({(user.storageUsedBytes / 1024 / 1024).toFixed(1)} MB)
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleViewDetails(user)}
                        className="text-blue-600 hover:text-blue-900 text-sm"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => handleChangePlan(user.id)}
                        className="text-green-600 hover:text-green-900 text-sm"
                      >
                        Change Plan
                      </button>
                      <button
                        onClick={() => handleApplyDiscount(user.id)}
                        className="text-purple-600 hover:text-purple-900 text-sm"
                      >
                        Discount
                      </button>
                      <button
                        onClick={() => handleManageLimits(user.id)}
                        className="text-orange-600 hover:text-orange-900 text-sm"
                      >
                        Limits
                      </button>
                      <button
                        onClick={() => handleCreateInvoice(user.id)}
                        className="text-indigo-600 hover:text-indigo-900 text-sm"
                      >
                        Invoice
                      </button>
                      {user.status === 'ACTIVE' ? (
                        <button
                          onClick={() => handleSuspend(user)}
                          className="text-yellow-600 hover:text-yellow-900 text-sm"
                        >
                          Suspend
                        </button>
                      ) : user.status === 'SUSPENDED' ? (
                        <button
                          onClick={() => handleActivate(user)}
                          className="text-green-600 hover:text-green-900 text-sm"
                        >
                          Activate
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {page + 1} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Details Modal (Tabbed) */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">User Details</h2>
                <button onClick={() => setShowDetailsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b mb-4">
                {(['profile', 'analytics', 'invoices'] as DetailsTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setDetailsTab(tab)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      detailsTab === tab
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab === 'profile' ? 'Profile' : tab === 'analytics' ? 'Analytics' : 'Invoices'}
                  </button>
                ))}
              </div>

              {/* Profile Tab */}
              {detailsTab === 'profile' && (
                <>
                  {loadingDetails ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Email</label>
                          <div className="font-medium">{selectedUser.email}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Status</label>
                          <div>{getStatusBadge(selectedUser.status)}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Name</label>
                          <div className="font-medium">
                            {selectedUser.firstName} {selectedUser.lastName}
                          </div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Entity Type</label>
                          <div>{selectedUser.entityType || '-'}</div>
                        </div>
                        {selectedUser.companyName && (
                          <>
                            <div>
                              <label className="text-sm text-gray-500">Company</label>
                              <div>{selectedUser.companyName}</div>
                            </div>
                            <div>
                              <label className="text-sm text-gray-500">Company ID</label>
                              <div>{selectedUser.companyId}</div>
                            </div>
                          </>
                        )}
                      </div>

                      {selectedUser.subscription && (
                        <div className="border-t pt-4 mt-4">
                          <h3 className="font-bold mb-2">Subscription</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-gray-500">Plan</label>
                              <div className="font-medium">{selectedUser.subscription.planName}</div>
                            </div>
                            <div>
                              <label className="text-sm text-gray-500">Status</label>
                              <div>{getSubscriptionBadge(selectedUser.subscription.status)}</div>
                            </div>
                            <div>
                              <label className="text-sm text-gray-500">Billing Cycle</label>
                              <div>{selectedUser.subscription.billingCycle || '-'}</div>
                            </div>
                            <div>
                              <label className="text-sm text-gray-500">Discount</label>
                              <div>{selectedUser.subscription.discountPercent ? `${selectedUser.subscription.discountPercent}%` : '-'}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-bold mb-2">Usage</h3>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-2xl font-bold">{selectedUser.widgetCount}</div>
                            <div className="text-sm text-gray-500">Widgets</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-2xl font-bold">{selectedUser.apiKeyCount}</div>
                            <div className="text-sm text-gray-500">API Keys</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <div className="text-2xl font-bold">{selectedUser.dataFileCount}</div>
                            <div className="text-sm text-gray-500">Files</div>
                          </div>
                        </div>
                      </div>

                      {selectedUser.featureOverrides.length > 0 && (
                        <div className="border-t pt-4 mt-4">
                          <h3 className="font-bold mb-2">Feature Overrides</h3>
                          <div className="space-y-2">
                            {selectedUser.featureOverrides.map((override: FeatureOverrideResponse) => (
                              <div key={override.id} className="flex justify-between items-center bg-gray-50 rounded px-3 py-2">
                                <span>{override.featureDisplayName}</span>
                                <span className="font-medium">
                                  {override.isUnlimited ? 'Unlimited' : override.customValue}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Analytics Tab */}
              {detailsTab === 'analytics' && (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <PeriodSelector value={analyticsPeriod} onChange={setAnalyticsPeriod} />
                  </div>

                  {/* Stat Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      {loadingAnalyticsOverview ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-indigo-700">
                            {formatNumber(userAnalyticsOverview?.totalConversations || 0)}
                          </div>
                          <div className="text-sm text-indigo-600">Conversations</div>
                          {userAnalyticsOverview?.conversationsToday ? (
                            <div className="text-xs text-indigo-500 mt-1">+{userAnalyticsOverview.conversationsToday} today</div>
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className="bg-violet-50 rounded-lg p-4 text-center">
                      {loadingAnalyticsOverview ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-violet-700">
                            {formatNumber(userAnalyticsOverview?.totalMessages || 0)}
                          </div>
                          <div className="text-sm text-violet-600">Messages</div>
                          {userAnalyticsOverview?.messagesToday ? (
                            <div className="text-xs text-violet-500 mt-1">+{userAnalyticsOverview.messagesToday} today</div>
                          ) : null}
                        </>
                      )}
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      {loadingAnalyticsOverview ? (
                        <Spinner size="sm" />
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-amber-700">
                            {formatNumber(userAnalyticsOverview?.totalTokens || 0)}
                          </div>
                          <div className="text-sm text-amber-600">Tokens</div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4">
                      {loadingAnalyticsTraffic ? (
                        <div className="flex items-center justify-center h-[250px]"><Spinner /></div>
                      ) : (
                        <TrendLineChart
                          data={userAnalyticsTraffic?.conversationTrend || []}
                          title="Conversation Trend"
                          color="#6366f1"
                          height={250}
                        />
                      )}
                    </Card>
                    <Card className="p-4">
                      {loadingAnalyticsTraffic ? (
                        <div className="flex items-center justify-center h-[250px]"><Spinner /></div>
                      ) : (
                        <ChannelBarChart
                          data={userAnalyticsTraffic?.conversationsByChannel || {}}
                          title="By Channel"
                          height={250}
                        />
                      )}
                    </Card>
                    <Card className="p-4">
                      {loadingAnalyticsTraffic ? (
                        <div className="flex items-center justify-center h-[250px]"><Spinner /></div>
                      ) : (
                        <TrendLineChart
                          data={userAnalyticsTraffic?.messageTrend || []}
                          title="Message Trend"
                          color="#8b5cf6"
                          height={250}
                        />
                      )}
                    </Card>
                    <Card className="p-4">
                      {loadingAnalyticsTraffic ? (
                        <div className="flex items-center justify-center h-[250px]"><Spinner /></div>
                      ) : (
                        <TrendLineChart
                          data={userAnalyticsTraffic?.tokenTrend || []}
                          title="Token Trend"
                          color="#f59e0b"
                          height={250}
                        />
                      )}
                    </Card>
                  </div>
                </div>
              )}

              {/* Invoices Tab */}
              {detailsTab === 'invoices' && (
                <div>
                  {loadingUserInvoices ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : userInvoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No invoices found for this user</div>
                  ) : (
                    <>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Paid At</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {userInvoices.map((inv: InvoiceAdminListResponse) => (
                            <tr key={inv.id}>
                              <td className="px-4 py-3 text-sm font-medium">{inv.invoiceNumber}</td>
                              <td className="px-4 py-3 text-sm">{inv.total.toFixed(2)}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  inv.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                  inv.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                  inv.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {inv.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {formatDate(inv.dueDate)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {formatDate(inv.paidAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {invoiceTotalPages > 1 && (
                        <div className="flex justify-center gap-2 mt-4">
                          <button
                            onClick={() => setInvoicePage(p => Math.max(0, p - 1))}
                            disabled={invoicePage === 0}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 text-sm">
                            Page {invoicePage + 1} of {invoiceTotalPages}
                          </span>
                          <button
                            onClick={() => setInvoicePage(p => Math.min(invoiceTotalPages - 1, p + 1))}
                            disabled={invoicePage >= invoiceTotalPages - 1}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Plan Modal */}
      {showChangePlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">Change User Plan</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Plan</label>
                <select
                  value={newPlanCode}
                  onChange={(e) => setNewPlanCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">Select a plan</option>
                  {plans.map((plan: PlanAdminResponse) => (
                    <option key={plan.code} value={plan.code}>{plan.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={planChangeReason}
                  onChange={(e) => setPlanChangeReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="Optional reason for the change"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowChangePlanModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitChangePlan}
                disabled={!newPlanCode || changeUserPlan.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {changeUserPlan.isPending ? 'Changing...' : 'Change Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">Apply Discount</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
                <input
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (months)</label>
                <input
                  type="number"
                  value={discountDuration}
                  onChange={(e) => setDiscountDuration(parseInt(e.target.value) || 1)}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <textarea
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="Optional reason for the discount"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDiscountModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDiscount}
                disabled={applyDiscount.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {applyDiscount.isPending ? 'Applying...' : 'Apply Discount'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-4">Create Invoice</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Plan</label>
                <select
                  value={invoiceTargetPlan}
                  onChange={(e) => setInvoiceTargetPlan(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  {plans.map((plan: PlanAdminResponse) => (
                    <option key={plan.code} value={plan.code}>{plan.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., 500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={invoiceCurrency}
                  onChange={(e) => setInvoiceCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="GEL">GEL (₾)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="Optional notes for the invoice"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitInvoice}
                disabled={!invoiceAmount || createInvoice.isPending}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {createInvoice.isPending ? 'Creating...' : 'Create & Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Limits Modal */}
      {showFeatureLimitsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Feature Limits</h2>
                <button onClick={() => setShowFeatureLimitsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingLimits ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : featureLimits ? (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    Plan: <span className="font-medium">{featureLimits.planName}</span>
                  </div>

                  {/* Feature limits table */}
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Feature</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan Default</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Effective</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(featureLimits.limits || []).map((limit: FeatureLimitInfo) => (
                        <tr key={limit.featureCode} className={limit.hasOverride ? 'bg-orange-50' : ''}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{limit.displayName}</div>
                            <div className="text-xs text-gray-500">{limit.featureCode}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {limit.isBooleanFeature
                              ? (limit.planValue ? 'Yes' : 'No')
                              : limit.planValue}
                          </td>
                          <td className="px-4 py-3">
                            {editingFeature === limit.featureCode ? (
                              <div className="flex items-center gap-2">
                                {limit.isBooleanFeature ? (
                                  <select
                                    value={overrideValue}
                                    onChange={(e) => setOverrideValue(parseInt(e.target.value))}
                                    className="px-2 py-1 border rounded text-sm"
                                  >
                                    <option value="0">No</option>
                                    <option value="1">Yes</option>
                                  </select>
                                ) : (
                                  <>
                                    <input
                                      type="number"
                                      value={overrideValue}
                                      onChange={(e) => setOverrideValue(parseInt(e.target.value) || 0)}
                                      disabled={overrideUnlimited}
                                      className="w-20 px-2 py-1 border rounded text-sm"
                                    />
                                    <label className="flex items-center gap-1 text-xs">
                                      <input
                                        type="checkbox"
                                        checked={overrideUnlimited}
                                        onChange={(e) => setOverrideUnlimited(e.target.checked)}
                                      />
                                      Unlimited
                                    </label>
                                  </>
                                )}
                              </div>
                            ) : (
                              <span className={`font-medium ${limit.hasOverride ? 'text-orange-600' : 'text-gray-900'}`}>
                                {limit.isUnlimited
                                  ? 'Unlimited'
                                  : limit.isBooleanFeature
                                    ? (limit.effectiveValue ? 'Yes' : 'No')
                                    : limit.effectiveValue}
                                {limit.hasOverride && <span className="text-xs ml-1">(custom)</span>}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {editingFeature === limit.featureCode ? (
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => setEditingFeature(null)}
                                  className="text-gray-600 hover:text-gray-900 text-sm"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveOverride}
                                  disabled={createOverride.isPending || updateOverride.isPending}
                                  className="text-blue-600 hover:text-blue-900 text-sm"
                                >
                                  Save
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleEditFeature(limit.featureCode, limit.effectiveValue, limit.isUnlimited)}
                                  className="text-blue-600 hover:text-blue-900 text-sm"
                                >
                                  Edit
                                </button>
                                {limit.hasOverride && (
                                  <button
                                    onClick={() => handleRemoveOverride(limit.featureCode)}
                                    className="text-red-600 hover:text-red-900 text-sm"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Add new override section */}
                  {!editingFeature && (
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-medium mb-3">Add Custom Limit</h3>
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <label className="block text-sm text-gray-600 mb-1">Feature</label>
                          <select
                            value={overrideFeatureCode}
                            onChange={(e) => {
                              setOverrideFeatureCode(e.target.value);
                              setOverrideValue(0);
                              setOverrideUnlimited(false);
                            }}
                            className="w-full px-3 py-2 border rounded-lg bg-white"
                          >
                            <option value="">Select feature...</option>
                            {featureCodes
                              .filter((fc: FeatureCodeInfo) => !(featureLimits.limits || []).find((l: FeatureLimitInfo) => l.featureCode === fc.code && l.hasOverride))
                              .map((fc: FeatureCodeInfo) => (
                                <option key={fc.code} value={fc.code}>{fc.displayName}</option>
                              ))}
                          </select>
                        </div>
                        <div className="w-24">
                          <label className="block text-sm text-gray-600 mb-1">Value</label>
                          <input
                            type="number"
                            value={overrideValue}
                            onChange={(e) => setOverrideValue(parseInt(e.target.value) || 0)}
                            disabled={overrideUnlimited}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                        <label className="flex items-center gap-1 pb-2">
                          <input
                            type="checkbox"
                            checked={overrideUnlimited}
                            onChange={(e) => setOverrideUnlimited(e.target.checked)}
                          />
                          <span className="text-sm">Unlimited</span>
                        </label>
                        <button
                          onClick={handleSaveOverride}
                          disabled={!overrideFeatureCode || createOverride.isPending}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No limits data available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
